// Prices come from the panel (NEXT_PUBLIC_PANEL_URL -> Administration -> Цены за ресурсы),
// the prices customers are actually charged by. A resource type is linked to a
// panel price through its "CloudStack название" (cpu, memory, primary_storage,
// public_ip, ...) or the panel's product code itself (vm_cpu, bucket_gb_month, ...).
// When the panel cannot be reached, the price saved on the site is used.

// The panel's address: prices, sign-in and sign-up all go there. NEXT_PUBLIC_ is
// built into the pages, so a new address needs a rebuild.
export const PANEL_URL = (process.env.NEXT_PUBLIC_PANEL_URL || 'https://console.oblako.kg').replace(/\/+$/, '');

/** Hours in a month, as the panel counts them for monthly prices. */
export const HOURS_PER_MONTH = 730;

export interface PanelPrice {
  name: string;
  unit: string;
  period: 'hour' | 'month' | 'unit';
  price: number;
  /** Per hour, or null for a price that is not charged by time (traffic). */
  hourly: number | null;
  monthly: number | null;
}

export type PanelPrices = Record<string, PanelPrice>;

/** CloudStack resource names the site uses -> the panel's product codes. */
const PRODUCT_BY_NAME: Record<string, string> = {
  cpu: 'vm_cpu',
  vcpu: 'vm_cpu',
  cpu_cores: 'vm_cpu',
  memory: 'vm_ram',
  ram: 'vm_ram',
  primary_storage: 'volume_gb_hour',
  volume: 'volume_gb_hour',
  disk: 'volume_gb_hour',
  public_ip: 'public_ip_hour',
  publicip: 'public_ip_hour',
  ip: 'public_ip_hour',
  ips: 'public_ip_hour',
  object_storage: 'bucket_gb_month',
  s3: 'bucket_gb_month',
  bucket: 'bucket_gb_month',
  snapshot: 'snapshot_gb_hour',
};

/** The panel's prices, or null when it cannot be reached within 3 s. Cached for a minute. */
export async function fetchPanelPrices(): Promise<PanelPrices | null> {
  try {
    const res = await fetch(`${PANEL_URL}/api/public/prices`, {
      headers: { Accept: 'application/json' },
      // A stalled panel must not hold the page: give up and use the site's prices.
      signal: AbortSignal.timeout(3000),
      next: { revalidate: 60 },
    } as RequestInit);
    if (!res.ok) return null;
    const json = await res.json();
    return json && typeof json.prices === 'object' && json.prices !== null ? (json.prices as PanelPrices) : null;
  } catch (err) {
    console.error('Panel prices are unavailable:', err);
    return null;
  }
}

/** The panel product a resource type is linked to, if any. */
export function panelProductFor(cloudstackName: string | null | undefined): string | null {
  const name = (cloudstackName || '').trim().toLowerCase();
  if (!name) return null;
  return PRODUCT_BY_NAME[name] ?? name;
}

/** The panel's price per hour for a resource type, or null when the panel has none for it. */
export function panelHourlyPrice(
  prices: PanelPrices | null,
  cloudstackName: string | null | undefined,
): number | null {
  const code = panelProductFor(cloudstackName);
  if (!prices || !code) return null;
  const hourly = prices[code]?.hourly;
  return typeof hourly === 'number' && Number.isFinite(hourly) ? hourly : null;
}

/** The panel's price per month for a product code, or null when the panel has none. */
export function panelMonthlyPrice(prices: PanelPrices | null, code: string): number | null {
  const monthly = prices?.[code]?.monthly;
  return typeof monthly === 'number' && Number.isFinite(monthly) ? monthly : null;
}

/** "1.46 сом" with as many decimals as small prices need. */
export function formatSom(amount: number): string {
  const digits = amount >= 10 ? 0 : amount >= 1 ? 2 : amount >= 0.01 ? 3 : 4;
  const text = amount.toFixed(digits);
  // Only the decimal padding goes: 1.500 -> 1.5, but 10 stays 10.
  return `${digits > 0 ? text.replace(/\.?0+$/, '') : text} сом`;
}
